import { store } from "../redux/store/configureStore";
import { Setting } from "./../utils/Setting";

// Endpoints for the scope schedule editor (Create/Edit Schedule flow).
// These mirror the portal's apiV1 scope-schedule endpoints.
const SCHEDULE_EDITOR_ENDPOINTS = {
  GET_SCHEDULE: (scopeId) => `api/scope/${scopeId}/schedule`,
  PREVIEW_SCHEDULE: (scopeId) => `api/scope/${scopeId}/schedule/preview`,
  APPLY_SCHEDULE: (scopeId) => `api/scope/${scopeId}/schedule/apply`,
};

/**
 * Thin fetch wrapper used instead of `getApiData` because the schedule
 * preview/apply endpoints return their error details (blockers, message)
 * in the response BODY of non-2xx responses. `getApiData` collapses those
 * into `new Error(err.message)`, losing the blockers — this wrapper
 * rejects with the parsed body instead so callers can read
 * `err.blockers` / `err.message` exactly like the portal does.
 */
const request = async (endpoint, method = "GET", body = null) => {
  const {
    auth: { token },
  } = store.getState();

  const params = {
    method,
    headers: {
      "Content-Type": "application/json",
      "cache-control":
        "no-store, no-cache, must-revalidate, post-check=0, pre-check=0",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };
  if (body != null) params.body = JSON.stringify(body);

  const response = await fetch(Setting.api + endpoint, params);
  let json = null;
  try {
    json = await response.json();
  } catch (e) {
    json = null;
  }
  if (!response.ok) {
    // Reject with the parsed body (carries blockers/message) when present.
    // eslint-disable-next-line no-throw-literal
    throw json || { message: "Unexpected error" };
  }
  return json;
};

/**
 * Load the full schedule for a scope: activities, billing milestones,
 * contractor payouts, customer payments, project/bid windows, editability.
 * @param {number|string} scopeId
 * @returns {Promise<object>} `{ success, data }` — data is the schedule
 */
export const getScopeSchedule = async (scopeId) => {
  try {
    return await request(SCHEDULE_EDITOR_ENDPOINTS.GET_SCHEDULE(scopeId));
  } catch (error) {
    console.error("Error fetching scope schedule:", error);
    throw error;
  }
};

/**
 * Dry-run the pending edits. Returns `{ can_apply, blockers, warnings,
 * locked, plan }` — the plan carries projected dates for derived rows.
 * @param {number|string} scopeId
 * @param {object} payload `{ activities, billing_milestones,
 *   customer_payments, reason_category }`
 */
export const previewScopeSchedule = async (scopeId, payload) => {
  return request(
    SCHEDULE_EDITOR_ENDPOINTS.PREVIEW_SCHEDULE(scopeId),
    "POST",
    payload,
  );
};

/**
 * Persist the pending edits. Same payload as preview plus `notes`.
 * Fails with 409 when the scope left ready_to_start, 422 with `blockers`.
 * @param {number|string} scopeId
 * @param {object} payload
 * @returns {Promise<object>} `{ success, data: { written, payment_lines_result } }`
 */
export const applyScopeSchedule = async (scopeId, payload) => {
  return request(
    SCHEDULE_EDITOR_ENDPOINTS.APPLY_SCHEDULE(scopeId),
    "POST",
    payload,
  );
};
